import { useToast } from '@afterpaytouch/core'
import { TFunction } from 'next-i18next'
import { useContext } from 'react'
import { useAmplitudeWithEnduringEventProperties } from '../../integrations/amplitude'
import { TrackingEvent } from '../../model/amplitude'
import { useCancelBillingAgreementMutation } from '../../state'
import { RecurringPaymentsContext, RecurringPaymentTabs } from '../RecurringPaymentsList/RecurringPaymentsContext'

export interface UseCancelAgreementLogicProps {
  agreementId: number
  merchantName: String
  t: TFunction
  closeModal: () => void
}

export interface UseCancelAgreementLogicReturn {
  useCancelBillingAgreement: () => Promise<void>
}

export const useCancelAgreementLogic = ({ agreementId, merchantName, t, closeModal }: UseCancelAgreementLogicProps): UseCancelAgreementLogicReturn => {
  const [cancelBillingAgreement] = useCancelBillingAgreementMutation()
  const { setTabValue } = useContext(RecurringPaymentsContext)
  const { logEvent } = useAmplitudeWithEnduringEventProperties()
  const setToast = useToast()

  const useCancelBillingAgreement = async (): Promise<void> => {
    try {
      await cancelBillingAgreement(agreementId).unwrap()
      setToast({
        message: t('recurringPayments:billingAgreementDetail:cancelModal:success', { merchantName }),
        kind: 'Success',
        testNameSpace: 'cancel-agreement-toast',
      })
      closeModal()
      // After billing agreement cancelled, show inactive tab
      setTabValue(RecurringPaymentTabs.Inactive)
    } catch (e) {
      const errorCode = e?.data?.errorCode ?? 'unspecified_error'
      logEvent(TrackingEvent.CANCEL_BILLING_AGREEMENT_FAILED, {
        error: errorCode,
      })
      setToast({
        message: e?.data?.message ?? t('recurringPayments:billingAgreementDetail:cancelModal:fail'),
        kind: 'Failure',
        testNameSpace: 'cancel-agreement-toast-error',
      })
    }
  }
  return { useCancelBillingAgreement }
}
